import { getToken, clearToken } from '../utils/authToken';
import { copyToClipboard } from '../utils/clipboard';
import { useState, useEffect, useRef } from 'react';
import { Sparkles, Loader2, Copy, Trash2, Check, Save, FileText, Play, Monitor, Edit3, X, RotateCcw, Wrench, ImagePlus, SendHorizontal } from 'lucide-react';
import axios from 'axios';
import Avatar from './Avatar';
import MessageContent from './MessageContent';
import StreamingText from './StreamingText';
import CodeAnalysisResults from './CodeAnalysisResults';
import ReviewBox from './ReviewBox';
import ProjectAdaptPrompt from './ProjectAdaptPrompt';
import ProjectGitHubImportPrompt from './ProjectGitHubImportPrompt';
import AuditScreenshotsBlock from './AuditScreenshotsBlock';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { navigateToPremium } from '../utils/navigateToPremium';
import { saveImageToGallery } from './ImageGallery';
// CUADRO NEGRO — Extracciones mínimas seguras a /message-bubble/.
import { ImageGallery } from './message-bubble/MessageBubbleMedia';
import { formatMessageTimestamp, formatFullTimestamp, isTutorialContent } from './message-bubble/utils';

import { BACKEND_URL } from '../config/api';
import { isDamyxActionBlock } from '../utils/damyxFence';
const API = `${BACKEND_URL}/api`;

// 🟥 CUADRO NEGRO — Auto-apertura automática de elementos del Workspace.
// Guard module-level para evitar que un mismo bloque `damyxia-action`
// se ejecute más de una vez (StrictMode double-invoke, re-renders,
// re-mounts al navegar entre paneles). Se guarda un hash simple del
// contenido del mensaje + índice del fence.
const _autoFiredActionKeys = new Set();
// P26.10 · Regex ESTRICTO para el fence damyxia-action.
// El cierre ``` debe estar en línea propia (precedido de \n y seguido
// por \n o fin de string) para evitar matcheo prematuro con backticks
// internos que aparecen dentro del content JSON del payload.
const _fenceRegexGlobal = /```damyxia-action[^\n]*\n([\s\S]+?)\n```(?=\n|$)/g;
function _hashKey(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h * 31 + str.charCodeAt(i)) | 0;
  }
  return h.toString(36);
}

const MessageBubble = ({ message, conversationId, previousMessage, onCodeFix, onDeleteMessage, onExecuteCode, isNewMessage = false, onCollapseMessage, messageIdx }) => {
  const { language, t } = useLanguage();
  const [codeAnalysis, setCodeAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [hasAnalyzed, setHasAnalyzed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [codeExecuted, setCodeExecuted] = useState(false);
  const [imageSaved, setImageSaved] = useState(false);
  const [isPublishingToWorkspace, setIsPublishingToWorkspace] = useState(false);
  const [publishedToWorkspace, setPublishedToWorkspace] = useState(false);
  // 🟥 CUADRO NEGRO 2026-06-28 (rev10) — Guard anti-doble-fire en mobile.
  // En iOS/Android, un tap puede disparar `touchstart` y `click` casi
  // simultáneamente. Si ambos llaman el publish, vemos errores duplicados.
  // Usamos un ref temporal (no state, no re-render) que bloquea llamadas
  // dentro de la misma ventana de 500ms.
  const lastUserImagePublishRef = useRef(0);
  // Estados para el botón "Enviar" (envía el contenido de la respuesta al
  // Área de Trabajo como un módulo interno editable). Independiente del
  // flujo de publicación de imágenes existente.
  const [isSendingToWorkspace, setIsSendingToWorkspace] = useState(false);
  const [sentToWorkspace, setSentToWorkspace] = useState(false);
  
  // Estado para modo de edición de código
  const [isEditingCode, setIsEditingCode] = useState(false);
  const [editedCode, setEditedCode] = useState('');
  const [originalCode, setOriginalCode] = useState('');

  const showPDFButton = message.role === 'assistant' && isTutorialContent(message.content);

  // 🟥 CUADRO NEGRO — Detectar bloque `damyxia-action` para:
  //   (a) mostrar un badge visual "Acción del Área de Trabajo",
  //   (b) ocultar el JSON crudo del render (queda oculto pero disponible
  //       para el botón "Enviar" que lo despacha al endpoint).
  // P26.10 · Cierre estricto para evitar matcheo prematuro con backticks
  // internos del content JSX.
  const damyxiaActionMatch = message?.role === 'assistant' && typeof message?.content === 'string'
    ? message.content.match(/```damyxia-action[^\n]*\n([\s\S]+?)\n```(?=\n|$)/)
    : null;
  const hasDamyxiaAction = Boolean(damyxiaActionMatch);
  // Contar TODOS los bloques (soporte MACS)
  const damyxiaActionAllCount = hasDamyxiaAction
    ? (message.content.match(/```damyxia-action[^\n]*\n[\s\S]+?\n```(?=\n|$)/g) || []).length
    : 0;
  const damyxiaActionPreview = (() => {
    if (!hasDamyxiaAction) return null;
    try {
      const parsed = JSON.parse(damyxiaActionMatch[1].trim());

      // 🟥 iter438 · CUADRO NEGRO — SOLUCIÓN DEFINITIVA
      // ─────────────────────────────────────────────────
      // Si hay proyecto activo Y el fence es un tipo prohibido con build
      // intent en el título, NO renderizamos la card TAREA/NOTA/DOC.
      // El evento se envía silenciosamente al CREATOR_ERROR_LOG.
      // El usuario final NO ve nada — sólo Marcelo verá el registro.
      try {
        let _activeMid = null;
        if (typeof window !== 'undefined') {
          _activeMid = window.__damyxiaActiveModuleId
            || (typeof localStorage !== 'undefined' && localStorage.getItem('damyxia:active-module-id'))
            || null;
        }
        const _t = String(parsed.type || '').toLowerCase();
        const _title = String(parsed.title || '');
        const _content = String(parsed.content || '');
        const _buildIntentRe = /\b(app|apps|aplicaci[oó]n(?:es)?|apk|web|sitio|p[aá]gina|landing|dashboard|panel|tienda|ecommerce|shop|blog|portal|sistema|plataforma|m[oó]dulo|componente|formulario|form|api|backend|frontend|servicio|bot|chatbot|crm|erp|saas|calculadora|juego|game|clon|clone|herramienta|utilidad|escaner|scanner|login|auth|autenticaci[oó]n)\b/i;

        // Layer iter438: type task/note/document con build intent.
        if (_activeMid && ['task', 'note', 'document'].includes(_t) && _buildIntentRe.test(_title)) {
          // Log privado al creador (fire-and-forget, no bloquea render).
          try {
            axios.post(`${API}/creator/errors`, {
              code: 'FRONTEND_PREVIEW_HIDDEN',
              severity: 'warning',
              source: 'frontend',
              module_active_id: _activeMid,
              attempted_type: _t,
              attempted_title: _title.slice(0, 200),
              user_message_preview: '(preview de fence bloqueado)',
              llm_response_preview: (damyxiaActionMatch[1] || '').slice(0, 1000),
              context: { where: 'damyxiaActionPreview', layer: 'iter438' },
            }).catch(() => {});
          } catch (_) { /* noop */ }
          return null; // ← NO renderiza la card TAREA
        }

        // 🟥 iter439 · Layer nueva: type:"project" SIN código real y con
        // título que parece instrucción del usuario. NO exige build_intent
        // porque títulos como "Modificá el proyecto activo..." no tienen
        // sustantivos build-intent (solo verbos + adjetivos).
        if (_activeMid && _t === 'project') {
          const _instructionVerbsRe = /\b(modific[aá]|edit[aá]|agreg[aá]|añad[ií]|extend[eé]|continu[aá]|crear|hac[eé]|pon[eé]|sac[aá]|actualiz[aá])\b/i;
          const _hasCodeRe = /(?:function\s+\w+|=>\s*\{|=>\s*\(|return\s*\(|export\s+default|import\s+|<[A-Z][A-Za-z0-9]*|useState|useEffect|className=)/;
          const _titleLooksLikeInstruction = _instructionVerbsRe.test(_title) || _title.length > 80;
          const _hasCode = _hasCodeRe.test(_content);
          if (!_hasCode && _titleLooksLikeInstruction) {
            try {
              axios.post(`${API}/creator/errors`, {
                code: 'FRONTEND_PROJECT_HIDDEN',
                severity: 'error',
                source: 'frontend',
                module_active_id: _activeMid,
                attempted_type: 'project',
                attempted_title: _title.slice(0, 200),
                llm_response_preview: _content.slice(0, 1000),
                context: {
                  where: 'damyxiaActionPreview',
                  layer: 'iter439',
                  has_code: _hasCode,
                  title_looks_like_instruction: _titleLooksLikeInstruction,
                },
              }).catch(() => {});
            } catch (_) { /* noop */ }
            return null; // ← NO renderiza la card PROYECTO espurio
          }
        }
      } catch (_) { /* si algo falla, dejar render normal */ }

      const typeLabels = {
        note: language === 'es' ? 'Nota' : 'Note',
        document: language === 'es' ? 'Documento' : 'Document',
        task: language === 'es' ? 'Tarea' : 'Task',
        project: language === 'es' ? 'Proyecto' : 'Project',
      };
      const baseLabel = typeLabels[parsed.type] || (language === 'es' ? 'Elemento' : 'Item');
      // Si hay múltiples bloques, indicarlo (MACS)
      const label = damyxiaActionAllCount > 1
        ? (language === 'es'
            ? `${damyxiaActionAllCount} elementos`
            : `${damyxiaActionAllCount} items`)
        : baseLabel;
      return {
        typeLabel: label,
        title: parsed.title || '',
      };
    } catch (_) {
      return null;
    }
  })();
  const displayContent = hasDamyxiaAction && typeof message?.content === 'string'
    ? message.content.replace(/```damyxia-action[^\n]*\n[\s\S]+?\n```(?=\n|$)/g, '').trim()
    : message?.content;

  // 🟥 CUADRO NEGRO — Auto-apertura automática de elementos del Workspace.
  // Cuando el asistente emite uno o más bloques `damyxia-action`, se
  // ejecutan AUTOMÁTICAMENTE (sin requerir el botón "Enviar") contra el
  // endpoint /api/workspace/execute-action. Al terminar, abrimos el
  // InternalWorkspace y seleccionamos el elemento recién creado en el
  // panel derecho. Idempotente por module-level Set (evita doble-fire
  // en StrictMode/re-mounts).
  const [autoFireState, setAutoFireState] = useState('idle'); // idle|running|done|error
  const [autoFireCreated, setAutoFireCreated] = useState(null); // { name, type }
  useEffect(() => {
    if (message?.role !== 'assistant') return;
    if (typeof message?.content !== 'string') return;
    if (!message.content.includes('```damyxia-action')) return;
    // NOTA: No bloqueamos por isNewMessage — el guard idempotente
    // (_autoFiredActionKeys) evita re-fire si el useEffect corre múltiples
    // veces durante el streaming. El regex sólo matchea fences cerradas.

    // Parse todos los fences en orden.
    const payloads = [];
    let match;
    _fenceRegexGlobal.lastIndex = 0;
    while ((match = _fenceRegexGlobal.exec(message.content)) !== null) {
      try {
        const parsed = JSON.parse(match[1].trim());
        const type = String(parsed.type || '').toLowerCase();
        const title = String(parsed.title || '').trim();
        if (!['note', 'document', 'task', 'project'].includes(type)) continue;
        if (!title) continue;
        payloads.push({
          action: parsed.action === 'update' ? 'update' : 'create',
          type,
          title,
          content: typeof parsed.content === 'string' ? parsed.content : '',
          // P26.10 · pasar target_id top-level para action=update
          target_id: typeof parsed.target_id === 'string' ? parsed.target_id : undefined,
          // 🟩 P26.11 · Multi-archivo + render_mode/output_mode/runtime
          files: Array.isArray(parsed.files) ? parsed.files : undefined,
          render_mode: typeof parsed.render_mode === 'string' ? parsed.render_mode : (typeof parsed.renderMode === 'string' ? parsed.renderMode : undefined),
          output_mode: typeof parsed.output_mode === 'string' ? parsed.output_mode : (typeof parsed.outputMode === 'string' ? parsed.outputMode : undefined),
          runtime: typeof parsed.runtime === 'string' ? parsed.runtime : undefined,
          metadata: (parsed.metadata && typeof parsed.metadata === 'object') ? parsed.metadata : {},
          // 🟥 2026-02-06 · conversation_id para execution_logs → reconstruct
          conversation_id: conversationId || undefined,
        });
      } catch (_) { /* skip malformed */ }
    }
    if (payloads.length === 0) return;

    // Guard idempotente: clave por (message.id o timestamp o hash del content)
    // + índice del payload dentro del mensaje.
    const baseKey = String(
      message.id || message.timestamp || message.created_at || _hashKey(message.content)
    );
    const anyToFire = payloads.some((_, i) => !_autoFiredActionKeys.has(`${baseKey}#${i}`));
    if (!anyToFire) return;

    // Auth check silencioso (para invitados no disparar).
    const token = getToken();
    if (!token || token === 'guest') return;

    setAutoFireState('running');
    (async () => {
      let projectModuleId = null;    // 🟩 P26.21 · project tiene prioridad
      let projectData = null;
      let lastModuleId = null;
      let lastData = null;
      let firedCount = 0;

      // 🟥 iter437 · DEFENSE-IN-DEPTH · Bloqueo temprano en frontend
      // ────────────────────────────────────────────────────────────
      // Si hay proyecto activo en el workspace Y el LLM emitió un fence
      // type:"task" / "note" / "document" con título que describe una
      // modificación de código, NO llamamos al backend. El endpoint YA
      // lo rechaza con 422, pero llegar hasta ahí gasta budget del turno
      // y confunde al usuario con el card TAREA renderizándose primero.
      // Bloqueamos localmente + mostramos toast con instrucción clara.
      let activeModuleId = null;
      try {
        activeModuleId = window.__damyxiaActiveModuleId
          || window.localStorage.getItem('damyxia:active-module-id')
          || null;
      } catch (_) { /* no window/storage → skip */ }
      const _hasBuildIntent = (title) => {
        // Espejo mínimo de orchestrator/guardrail.py _BUILD_TARGET_RE.
        if (!title || typeof title !== 'string') return false;
        return /\b(app|apps|aplicaci[oó]n(?:es)?|apk|web|sitio|p[aá]gina|landing|dashboard|panel|tienda|ecommerce|shop|blog|portal|sistema|plataforma|m[oó]dulo|componente|formulario|form|api|backend|frontend|servicio|bot|chatbot|crm|erp|saas|calculadora|juego|game|clon|clone|herramienta|utilidad|escaner|scanner|login|auth|autenticaci[oó]n)\b/i.test(title);
      };

      for (let i = 0; i < payloads.length; i++) {
        const key = `${baseKey}#${i}`;
        if (_autoFiredActionKeys.has(key)) continue;
        _autoFiredActionKeys.add(key);

        // 🟥 iter438 · block SILENCIOSO ANTES de llamar al backend.
        // El usuario final NO ve error rojo — el evento va al canal
        // privado del creador (Marcelo). Comportamiento visible:
        // simplemente no se hace nada.
        const _t = payloads[i]?.type;
        const _title = payloads[i]?.title || '';
        const _content = String(payloads[i]?.content || '');
        if (activeModuleId && ['task', 'note', 'document'].includes(_t) && _hasBuildIntent(_title)) {
          _autoFiredActionKeys.delete(key);
          // Log silencioso al canal privado del creador.
          try {
            axios.post(`${API}/creator/errors`, {
              code: 'FRONTEND_BLOCKED',
              severity: 'warning',
              source: 'frontend',
              module_active_id: activeModuleId,
              attempted_type: _t,
              attempted_title: _title.slice(0, 200),
              context: { where: 'MessageBubble.autoFire', payload_index: i, layer: 'iter438' },
            }).catch(() => {});
          } catch (_) { /* noop */ }
          console.debug('[iter438] fence bloqueado silenciosamente', { type: _t });
          continue;
        }

        // 🟥 iter439 · block silencioso: type:"project" sin código real.
        if (activeModuleId && _t === 'project') {
          const _instructionVerbsRe = /\b(modific[aá]|edit[aá]|agreg[aá]|añad[ií]|extend[eé]|continu[aá]|crear|hac[eé]|pon[eé]|sac[aá]|actualiz[aá])\b/i;
          const _hasCodeRe = /(?:function\s+\w+|=>\s*\{|=>\s*\(|return\s*\(|export\s+default|import\s+|<[A-Z][A-Za-z0-9]*|useState|useEffect|className=)/;
          const _titleLooksLikeInstruction = _instructionVerbsRe.test(_title) || _title.length > 80;
          const _hasCode = _hasCodeRe.test(_content);
          if (!_hasCode && _titleLooksLikeInstruction) {
            _autoFiredActionKeys.delete(key);
            try {
              axios.post(`${API}/creator/errors`, {
                code: 'FRONTEND_BLOCKED_PROJECT_NO_CODE',
                severity: 'error',
                source: 'frontend',
                module_active_id: activeModuleId,
                attempted_type: 'project',
                attempted_title: _title.slice(0, 200),
                llm_response_preview: _content.slice(0, 1000),
                context: { where: 'MessageBubble.autoFire', payload_index: i, layer: 'iter439' },
              }).catch(() => {});
            } catch (_) { /* noop */ }
            console.debug('[iter439] project espurio bloqueado silenciosamente', { title: _title.slice(0, 60) });
            continue;
          }
        }

        try {
          const res = await axios.post(`${API}/workspace/execute-action`, payloads[i]);
          const data = res?.data;
          if (data && data.success && data.module_id) {
            lastModuleId = data.module_id;
            lastData = data;
            firedCount += 1;
            // 🟩 P26.21 · Si el payload era type:"project", ese es el
            // que se abre en el workspace (aunque venga primero en
            // la respuesta y luego haya documents espurios).
            if (payloads[i].type === 'project' && !projectModuleId) {
              projectModuleId = data.module_id;
              projectData = data;
            }
          }
        } catch (err) {
          // Si falla, quitamos la clave para permitir re-fire manual.
          _autoFiredActionKeys.delete(key);
          // 🟥 P26.42 · El compilador real rechazó el proyecto: mostramos el
          // error concreto (nunca un silencio ni un documento de consuelo).
          const d = err?.response?.data?.detail;
          if (d?.code === 'COMPILE_FAILED') {
            const first = d?.details?.errors?.[0];
            toast.error(
              `No se guardó: el proyecto no compila${first ? ` — ${first.file || ''} ${first.message || ''}` : ''}`,
              { duration: 8000 },
            );
          } else if (d?.code === 'OWNER_STRICT_BUILD_INTENT') {
            // 🟥 iter438 · el backend rechazó — log silencioso al canal
            // del creador. El usuario final NO ve el error.
            try {
              axios.post(`${API}/creator/errors`, {
                code: 'OWNER_STRICT_BUILD_INTENT',
                severity: 'error',
                source: 'backend',
                module_active_id: activeModuleId,
                attempted_type: d?.details?.attempted_type,
                attempted_title: (d?.details?.title || '').slice(0, 200),
                context: { where: 'MessageBubble.executeAction', backend_message: d?.message },
              }).catch(() => {});
            } catch (_) { /* noop */ }
          }
        }
      }
      // Priorizamos el project sobre el último creado.
      const openModuleId = projectModuleId || lastModuleId;
      const openData = projectData || lastData;
      if (openModuleId) {
        // 🟩 P26.22 · Distinguir UPDATE vs CREATE:
        //   - CREATE: despachar `damyxia:open-internal-workspace` para
        //     abrir el workspace + seleccionar el módulo.
        //   - UPDATE: NO reabrir el workspace (el módulo ya está abierto).
        //     Solo despachar `damyxia:internal-module-updated` para que
        //     el ModuleDetailPanel refresque en vivo SIN desmontarse.
        //     Esto elimina el flash "desaparece y reaparece".
        const isAllUpdate = payloads.length > 0 && payloads.every((p) => p.action === 'update');
        if (isAllUpdate) {
          try {
            window.dispatchEvent(new CustomEvent('damyxia:internal-module-updated', {
              detail: { moduleId: openModuleId, name: openData?.name },
            }));
          } catch (_) { /* noop */ }
        } else {
          // Flujo original de CREATE: abrir workspace + seleccionar el módulo.
          try {
            window.dispatchEvent(new Event('damyxia:open-internal-workspace'));
          } catch (_) { /* noop */ }
          setTimeout(() => {
            try {
              window.dispatchEvent(new CustomEvent('damyxia-open-internal-module', {
                detail: {
                  moduleId: openModuleId,
                  name: openData?.name,
                  detectedType: openData?.type,
                  source: 'auto_fire_damyxia_action',
                },
              }));
            } catch (_) { /* noop */ }
          }, 350);
        }
        setAutoFireCreated({ name: openData?.name, type: openData?.type, count: firedCount });
        setAutoFireState('done');
        setSentToWorkspace(true);
        setTimeout(() => setSentToWorkspace(false), 3000);
      } else {
        setAutoFireState('error');
      }
    })();
    // Depende del contenido del mensaje.
  }, [message?.content, message?.role]);

  // 🟥 CUADRO NEGRO 2026-06-27 (rev3 — fix mobile prod) — Helper para
  // extraer image_id desde image_url cuando el backend NO serializa
  // `image_id` por separado en mobile. Acepta:
  //   - "/api/workspace/image/<id>"        → <id>
  //   - "<BACKEND>/api/workspace/image/<id>" → <id>
  //   - "<id>"                              → <id>
  // Devuelve null si no se puede extraer.
  const extractImageIdFromUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    const m = url.match(/\/api\/workspace\/image\/([^/?#]+)/);
    if (m) return m[1];
    // Si no contiene path, asumir que es el id puro.
    if (!url.includes('/') && !url.startsWith('http') && !url.startsWith('data:')) {
      return url;
    }
    return null;
  };

  // Handler para "Enviar" del action bar (4 botones bajo cada dialogo).
  // - Si el mensaje (del usuario o del asistente) tiene imagen → publicar al
  //   Area de Trabajo (mismo flujo que el boton bajo la imagen).
  // - Si el mensaje del asistente NO tiene imagen pero el mensaje USUARIO
  //   anterior SI la tiene → publicar la imagen del usuario anterior.
  //   (caso típico: usuario sube foto → asistente responde "He recibido tu
  //   imagen. ¿Quieres que la envíe...?" → usuario pulsa Enviar bajo la
  //   respuesta del asistente.)
  // - Si solo hay texto en ambos → mandar el texto del mensaje actual a
  //   /workspace/auto que detecta el tipo (codigo/HTML/diagrama/texto).
  //
  // 🟥 CUADRO NEGRO — Canal estructurado Chat → Workspace.
  // Antes que cualquier otro caso, buscamos en la respuesta del asistente
  // un bloque `damyxia-action` (JSON) emitido por el LLM. Si existe, lo
  // despachamos al endpoint `/api/workspace/execute-action` para crear la
  // nota/documento/tarea/proyecto REAL en el Workspace.
  const parseDamyxiaActionBlock = (content) => {
    if (!content || typeof content !== 'string') return null;
    // P26.10 · Cierre estricto: ``` en línea propia (evita matcheo con
    // backticks internos del content JSX).
    const fenceRegex = /```damyxia-action[^\n]*\n([\s\S]+?)\n```(?=\n|$)/;
    const m = content.match(fenceRegex);
    if (!m) return null;
    try {
      const parsed = JSON.parse(m[1].trim());
      if (!parsed || typeof parsed !== 'object') return null;
      const action = String(parsed.action || 'create').toLowerCase();
      const type = String(parsed.type || '').toLowerCase();
      const title = String(parsed.title || '').trim();
      const allowed = ['note', 'document', 'task', 'project'];
      if (!allowed.includes(type)) return null;
      if (!title) return null;
      return {
        action: action === 'update' ? 'update' : 'create',
        type,
        title,
        content: typeof parsed.content === 'string' ? parsed.content : '',
        target_id: typeof parsed.target_id === 'string' ? parsed.target_id : undefined,
        metadata: (parsed.metadata && typeof parsed.metadata === 'object') ? parsed.metadata : {},
      };
    } catch (_) {
      return null;
    }
  };

  const handleSendToWorkspace = async () => {
    if (isSendingToWorkspace) return;

    // 🟥 CUADRO NEGRO — Prioridad máxima: bloque `damyxia-action`.
    const actionPayload = message?.role === 'assistant'
      ? parseDamyxiaActionBlock(message?.content)
      : null;
    if (actionPayload) {
      setIsSendingToWorkspace(true);
      try {
        const token = getToken();
        if (!token || token === 'guest') {
          toast.error(language === 'es' ? 'Inicia sesión para usar el área de trabajo' : 'Login to use workspace');
          return;
        }
        const res = await axios.post(
          `${API}/workspace/execute-action`,
          // 🟥 2026-02-06 · propagar conversation_id → execution_logs → reconstruct
          { ...actionPayload, conversation_id: conversationId || undefined },
          { headers: {} }
        );
        const data = res?.data || {};
        if (data.success && data.module_id) {
          window.dispatchEvent(new CustomEvent('damyxia-open-internal-module', {
            detail: {
              moduleId: data.module_id,
              name: data.name,
              detectedType: data.type,
              source: 'chat_execute_action',
            },
          }));
          setSentToWorkspace(true);
          const typeLabel = {
            note: language === 'es' ? 'Nota' : 'Note',
            document: language === 'es' ? 'Documento' : 'Document',
            task: language === 'es' ? 'Tarea' : 'Task',
            project: language === 'es' ? 'Proyecto' : 'Project',
          }[data.type] || (language === 'es' ? 'Elemento' : 'Item');
          toast.success(
            language === 'es'
              ? `${typeLabel} creado en el Área de Trabajo`
              : `${typeLabel} created in Workspace`
          );
          setTimeout(() => setSentToWorkspace(false), 3000);
        } else {
          toast.error(
            language === 'es'
              ? 'No se pudo ejecutar la acción del Área de Trabajo.'
              : 'Could not execute Workspace action.'
          );
        }
      } catch (err) {
        const raw = err?.response?.data?.detail;
        // 🟥 P26.42 · Error real del compilador de la plataforma.
        if (raw?.code === 'COMPILE_FAILED') {
          const first = raw?.details?.errors?.[0];
          toast.error(
            language === 'es'
              ? `No se guardó: el proyecto no compila${first ? ` — ${first.file || ''} ${first.message || ''}` : ''}`
              : `Not saved: the project does not compile${first ? ` — ${first.file || ''} ${first.message || ''}` : ''}`,
            { duration: 8000 },
          );
          return;
        }
        const detail = (typeof raw === 'string' ? raw : raw?.message) || err?.message || 'unknown';
        toast.error(
          language === 'es'
            ? `Error al ejecutar acción: ${detail}`
            : `Error executing action: ${detail}`
        );
      } finally {
        setIsSendingToWorkspace(false);
      }
      return;
    }

    // Resolver image_id efectivo: el del propio mensaje, o si es del
    // asistente y no tiene imagen, mirar el mensaje anterior del usuario.
    // 🟥 fix mobile prod: si image_id está vacío, intentar resolverlo
    // desde image_url (camino habitual cuando el backend mobile lo omite).
    const ownImageId = message?.image_id || extractImageIdFromUrl(message?.image_url) || null;
    const fallbackImageId = (
      !ownImageId &&
      message?.role === 'assistant' &&
      previousMessage?.role === 'user'
    ) ? (previousMessage?.image_id || extractImageIdFromUrl(previousMessage?.image_url)) : null;
    const effectiveImageId = ownImageId || fallbackImageId;

    // CUADRO NEGRO — Detectar imagen generada por DamyxIA (base64 en lugar
    // de image_id). Estas imágenes vienen del chat con herramienta de
    // generación de imagen y se persisten como base64 en el assistant_msg.
    // El handler debe hacer upload → publish (igual que ImageGallery), NO
    // caer al fallback /workspace/auto que crearía un módulo de TEXTO vacío
    // y abriría el workspace.
    const generatedBase64 = !effectiveImageId ? (
      message?.image_base64
      || (Array.isArray(message?.image_variations) && message.image_variations.length > 0
          ? (typeof message.image_variations[0] === 'string'
             ? message.image_variations[0]
             : message.image_variations[0]?.base64)
          : null)
    ) : null;

    const hasImage = Boolean(effectiveImageId);
    const hasGeneratedImage = Boolean(generatedBase64);
    const hasContent = Boolean(message?.content);
    if (!hasImage && !hasGeneratedImage && !hasContent) return;

    // CUADRO NEGRO — Si el assistant tiene una imagen embebida en markdown
    // (![...](...)) PERO no resolvimos image_id ni base64, NO caer al
    // fallback de texto: eso crearía un módulo de texto vacío y abriría
    // el workspace sin contenido. Mostrar error claro al usuario.
    // 🟦 2026-06-27 (fix mobile prod) — Extendido: detectar también
    // `image_url`, `has_image`, `image_base64_preview` en cualquier rol.
    // El bug en mobile prod era: un mensaje con image_url pero sin
    // image_id caía al case text → disparaba damyxia-open-internal-module
    // → abría el workspace SIN enviar la imagen.
    const looksLikeImageMessage =
      (message?.role === 'assistant' &&
        typeof message?.content === 'string' &&
        /!\[[^\]]*\]\([^)]+\)/.test(message.content)) ||
      Boolean(message?.image_url) ||
      Boolean(message?.has_image) ||
      Boolean(message?.image_base64_preview);
    if (!hasImage && !hasGeneratedImage && looksLikeImageMessage) {
      toast.error(
        language === 'es'
          ? 'No se pudo enviar la imagen. Falta image_id. Volvé a generar la imagen.'
          : 'Could not send the image. image_id missing. Please regenerate the image.'
      );
      return;
    }

    setIsSendingToWorkspace(true);
    try {
      const token = getToken();
      if (!token || token === 'guest') {
        toast.error(language === 'es' ? 'Inicia sesión para usar el área de trabajo' : 'Login to use workspace');
        return;
      }
      const headers = {};  // P2.1 cookie-only via axios.defaults

      // CUADRO NEGRO — Caso NUEVO: imagen generada por DamyxIA (base64 en
      // el assistant_msg). Hacer upload-image → publish para que el
      // workspace reciba la IMAGEN, no un módulo de texto.
      if (hasGeneratedImage) {
        const base64Data = generatedBase64.startsWith('data:')
          ? generatedBase64
          : `data:image/png;base64,${generatedBase64}`;
        const promptSnippet = (typeof message?.content === 'string' && message.content)
          ? message.content.substring(0, 200)
          : (language === 'es' ? 'Imagen generada por DamyxIA' : 'Image generated by DamyxIA');
        const uploadResponse = await axios.post(
          `${API}/workspace/upload-image`,
          {
            base64: base64Data,
            filename: `damyxia_generated_${Date.now()}.png`,
            instruction: promptSnippet,
          },
          { headers }
        );
        const uploadedImageId = uploadResponse.data?.image_id;
        if (!uploadedImageId) {
          toast.error(
            language === 'es'
              ? 'No se pudo subir la imagen generada.'
              : 'Could not upload the generated image.'
          );
          return;
        }
        const publishResponse = await axios.post(
          `${API}/workspace/publish`,
          { image_id: uploadedImageId },
          { headers }
        );
        if (publishResponse.data?.success || publishResponse.data?.workspace_item) {
          window.dispatchEvent(new CustomEvent('workspace-image-published', {
            detail: {
              workspaceItem: publishResponse.data.workspace_item,
              imageId: uploadedImageId,
              alreadyPublished: publishResponse.data.already_published,
            },
          }));
          setSentToWorkspace(true);
          toast.success(
            publishResponse.data.already_published
              ? (language === 'es' ? 'La imagen ya estaba en el área de trabajo' : 'Image already in workspace')
              : (language === 'es' ? 'Imagen enviada al área de trabajo' : 'Image sent to workspace')
          );
          setTimeout(() => setSentToWorkspace(false), 3000);
        }
        return;
      }

      // Caso 1: mensaje (o el anterior del usuario) con imagen → publicar
      // la imagen al Workspace.
      if (hasImage) {
        const res = await axios.post(
          `${API}/workspace/publish`,
          { image_id: effectiveImageId },
          { headers }
        );
        if (res.data?.success) {
          window.dispatchEvent(new CustomEvent('workspace-image-published', {
            detail: {
              workspaceItem: res.data.workspace_item,
              imageId: effectiveImageId,
              alreadyPublished: res.data.already_published,
            },
          }));
          setSentToWorkspace(true);
          toast.success(
            res.data.already_published
              ? (language === 'es' ? 'La imagen ya estaba en el área de trabajo' : 'Image already in workspace')
              : (language === 'es' ? 'Imagen enviada al área de trabajo' : 'Image sent to workspace')
          );
          setTimeout(() => setSentToWorkspace(false), 3000);
        }
        return;
      }

      // Caso 2: mensaje con solo texto → /workspace/auto (detecta tipo)
      const res = await axios.post(
        `${API}/workspace/auto`,
        {
          content: message.content,
          source: 'vision',
        },
        { headers }
      );
      const moduleId = res.data?.module_id;
      const moduleName = res.data?.name || 'Contenido';
      const detected = res.data?.detected_type || 'text';
      window.dispatchEvent(new CustomEvent('damyxia-open-internal-module', {
        detail: { moduleId, name: moduleName, detectedType: detected },
      }));
      setSentToWorkspace(true);
      toast.success(
        language === 'es'
          ? `Enviado al Área de Trabajo (${detected})`
          : `Sent to Workspace (${detected})`
      );
      setTimeout(() => setSentToWorkspace(false), 3000);
    } catch (error) {
      // Si la imagen ya esta en el workspace, mostrar un info en lugar de error.
      if (error?.response?.data?.already_published || /already/i.test(error?.response?.data?.detail || '')) {
        setSentToWorkspace(true);
        toast.info(language === 'es' ? 'La imagen ya está en el área de trabajo' : 'Image already in workspace');
        setTimeout(() => setSentToWorkspace(false), 3000);
        return;
      }
      const status = error?.response?.status;
      const detailRaw = error?.response?.data?.detail;
      const isPremiumLock = status === 403 && (
        (typeof detailRaw === 'object' && detailRaw?.code === 'PREMIUM_REQUIRED') ||
        (typeof detailRaw === 'string' && /premium/i.test(detailRaw))
      );
      const detail = typeof detailRaw === 'object' ? (detailRaw?.message || 'Premium requerido') : (detailRaw || error?.message || 'unknown');
      if (isPremiumLock) {
        toast.error(
          language === 'es'
            ? 'Enviar al Área de Trabajo es exclusivo Premium. Actualiza tu plan.'
            : 'Send to Workspace is Premium-only. Upgrade your plan.',
          {
            duration: 6000,
            action: {
              label: language === 'es' ? 'Actualizar' : 'Upgrade',
              onClick: navigateToPremium,
            },
          }
        );
      } else {
        toast.error(
          language === 'es'
            ? `Error al enviar al Área de Trabajo: ${detail}`
            : `Error sending to Workspace: ${detail}`
        );
      }
    } finally {
      setIsSendingToWorkspace(false);
    }
  };
  
  // Export message to PDF
  const handleExportPDF = async () => {
    if (!message.content) return;
    
    setIsExportingPDF(true);
    try {
      const token = getToken();
      const headers = {};  // P2.1 cookie-only: cookie httpOnly via axios.defaults
      
      // Extract title from content or use default
      let title = 'Tutorial DamyxIA';
      const titleMatch = message.content.match(/^#\s+(.+)$/m);
      if (titleMatch) {
        title = titleMatch[1].replace(/[*#]/g, '').trim();
      }
      
      const response = await axios.post(
        `${API}/chat/export-pdf`,
        {
          content: message.content,
          title: title,
          export_type: 'tutorial'
        },
        {
          headers,
          responseType: 'blob'
        }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${title.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 50)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('messages.pdfDownloaded'));
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error(t('messages.errorGeneratingPdf'));
    } finally {
      setIsExportingPDF(false);
    }
  };
  
  // Extract code blocks from the message for analysis
  const extractCodeBlocks = (content) => {
    // 🟥 P26.33 · Soportar lenguajes con guion y excluir los fences de
    // acción de DamyxIA (antes se colaban como JSX y rompían el preview).
    const codePattern = /```([\w+.#-]*)\n([\s\S]*?)```/g;
    const blocks = [];
    let match;

    while ((match = codePattern.exec(content)) !== null) {
      const lang = match[1] || 'javascript';
      const body = match[2].trim();
      if (isDamyxActionBlock(lang, body)) continue;
      // Skip non-executable code types
      if (!['bash', 'shell', 'cmd', 'text', 'json', 'yaml', 'md', 'markdown'].includes(lang.toLowerCase())) {
        blocks.push({
          language: lang.toLowerCase(),
          code: body,
        });
      }
    }
    return blocks;
  };
  
  const codeBlocks = message.role === 'assistant' && message.content 
    ? extractCodeBlocks(message.content) 
    : [];
  
  const hasCode = codeBlocks.length > 0;
  
  // Detectar si hay código React/JSX ejecutable en el workspace
  const hasExecutableCode = () => {
    if (!message.content || message.role !== 'assistant') return false;
    
    // Buscar bloques de código que contengan componentes React
    const codeBlockRegex = /```(jsx?|javascript|tsx?|react|js)?\n?([\s\S]*?)```/g;
    let match;
    
    while ((match = codeBlockRegex.exec(message.content)) !== null) {
      const lang = (match[1] || '').toLowerCase();
      const code = match[2] || '';
      
      // Ignorar código no ejecutable
      if (['bash', 'sh', 'shell', 'css', 'json', 'yaml', 'html', 'sql'].includes(lang)) continue;
      if (code.trim().startsWith('npx ') || code.trim().startsWith('npm ')) continue;
      
      // Detectar código React/JSX
      const isReactCode = code.includes('function ') || 
                         code.includes('const ') ||
                         code.includes('return (') ||
                         code.includes('return <') ||
                         code.includes('useState') ||
                         code.includes('useEffect') ||
                         code.includes('className=') ||
                         code.includes('onClick') ||
                         (code.includes('<div') && code.includes('</div>'));
      
      if (isReactCode) return true;
    }
    
    return false;
  };
  
  const showExecuteButton = hasExecutableCode();
  
  // Extraer código para edición
  const extractCodeForEditing = () => {
    const codeBlockRegex = /```(jsx?|javascript|tsx?|react|js)?\n?([\s\S]*?)```/g;
    const codeParts = [];
    let match;
    
    while ((match = codeBlockRegex.exec(message.content)) !== null) {
      const lang = (match[1] || '').toLowerCase();
      const code = match[2] || '';
      
      // Ignorar código no ejecutable
      if (['bash', 'sh', 'shell', 'css', 'json', 'yaml', 'html', 'sql'].includes(lang)) continue;
      if (code.trim().startsWith('npx ') || code.trim().startsWith('npm ')) continue;
      
      codeParts.push(code.trim());
    }
    
    return codeParts.join('\n\n');
  };
  
  // Iniciar modo de edición
  const handleStartEditing = () => {
    const code = extractCodeForEditing();
    setOriginalCode(code);
    setEditedCode(code);
    setIsEditingCode(true);
  };
  
  // Cancelar edición
  const handleCancelEditing = () => {
    setIsEditingCode(false);
    setEditedCode('');
    setOriginalCode('');
  };
  
  // Restaurar código original
  const handleResetCode = () => {
    setEditedCode(originalCode);
  };
  
  // Ejecutar código editado
  const handleExecuteEditedCode = () => {
    if (onExecuteCode && editedCode.trim()) {
      onExecuteCode(editedCode);
      setCodeExecuted(true);
      toast.success(t('messages.editedCodeExecuted'));
      setTimeout(() => setCodeExecuted(false), 3000);
      // No cerrar el editor para permitir más ajustes
    }
  };
  
  // Manejar ejecución de código
  const handleExecuteCode = () => {
    if (onExecuteCode) {
      // Extraer todo el código React/JSX del mensaje
      const codeBlockRegex = /```(jsx?|javascript|tsx?|react|js)?\n?([\s\S]*?)```/g;
      const codeParts = [];
      let match;
      
      while ((match = codeBlockRegex.exec(message.content)) !== null) {
        const lang = (match[1] || '').toLowerCase();
        const code = match[2] || '';
        
        // Ignorar código no ejecutable
        if (['bash', 'sh', 'shell', 'css', 'json', 'yaml', 'html', 'sql'].includes(lang)) continue;
        if (code.trim().startsWith('npx ') || code.trim().startsWith('npm ')) continue;
        
        codeParts.push(code.trim());
      }
      
      if (codeParts.length > 0) {
        const fullCode = codeParts.join('\n\n');
        onExecuteCode(fullCode);
        setCodeExecuted(true);
        toast.success(t('messages.codeSentToWorkspace'));
        setTimeout(() => setCodeExecuted(false), 3000);
      }
    } else {
      toast.info(t('messages.goToWorkspace'));
    }
  };
  
  // Auto-analyze code when message loads (only once)
  useEffect(() => {
    const analyzeCode = async () => {
      if (!hasCode || hasAnalyzed || isAnalyzing) return;
      
      // Small delay to not analyze immediately
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setIsAnalyzing(true);
      try {
        const token = getToken();
        const headers = {};  // P2.1 cookie-only: cookie httpOnly via axios.defaults
        
        const response = await axios.post(`${API}/code/analyze-response`, {
          ai_response: message.content,
          user_language: language
        }, { headers });
        
        if (response.data.analyzed) {
          setCodeAnalysis(response.data);
        }
      } catch (err) {
        console.error('Code analysis error:', err);
      } finally {
        setIsAnalyzing(false);
        setHasAnalyzed(true);
      }
    };
    
    analyzeCode();
  }, [hasCode, hasAnalyzed, isAnalyzing, message.content, language]);
  
  const handleApplyFix = (fixedCode, fixedLanguage) => {
    if (onCodeFix) {
      onCodeFix(fixedCode, fixedLanguage, message);
    }
  };
  
  if (message.role === 'user') {
    // Verificar si el mensaje tiene una imagen
    const hasImage = message.has_image || message.image_url || message.image_id || message.image_base64_preview;
    
    // Determinar la fuente de la imagen
    let imageUrl = null;
    
    if (message.image_base64_preview) {
      // Imagen temporal en base64 (antes de guardar en servidor)
      imageUrl = message.image_base64_preview;
    } else if (message.image_url) {
      // URL del servidor - verificar si ya tiene el prefijo completo
      if (message.image_url.startsWith('http')) {
        imageUrl = message.image_url;
      } else if (message.image_url.startsWith('/api/')) {
        imageUrl = `${BACKEND_URL}${message.image_url}`;
      } else {
        imageUrl = `${BACKEND_URL}/api/workspace/image/${message.image_url}`;
      }
    } else if (message.image_id) {
      // Construir URL desde image_id
      imageUrl = `${BACKEND_URL}/api/workspace/image/${message.image_id}`;
    }
    
    return (
      <div className="flex flex-col items-end" data-testid="user-message">
        <div className="bg-zinc-800/40 light:bg-zinc-100 text-zinc-100 light:text-zinc-900 rounded-2xl rounded-tr-sm px-5 py-3 max-w-[80%] backdrop-blur-sm border border-zinc-700/30 light:border-zinc-200">
          {/* Mostrar imagen si existe */}
          {hasImage && imageUrl && (
            <div className="mb-3">
              {/* 🟥 CUADRO NEGRO 2026-06-28 (rev10) — z-index 1 en el
                  contenedor de la imagen para que NUNCA tape al botón
                  "Enviar al Área de Trabajo" en mobile. */}
              <div className="relative" style={{ position: 'relative', zIndex: 1 }}>
                <img 
                  src={imageUrl}
                  alt="Imagen subida"
                  className="max-w-full max-h-64 rounded-lg object-contain bg-zinc-900/50 border border-zinc-600/30"
                  data-testid="user-uploaded-image"
                  onLoad={(e) => {
                    e.target.style.opacity = 1;
                  }}
                  onError={(e) => {
                    console.error('[MessageBubble] Error loading user image:', imageUrl);
                    // Mostrar placeholder en lugar de ocultar
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIj48cmVjdCBmaWxsPSIjMjgyODI4IiB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjNjY2IiBmb250LXNpemU9IjEyIj7wn5O3PC90ZXh0Pjwvc3ZnPg==';
                    e.target.className = 'w-20 h-20 rounded-lg opacity-50';
                  }}
                  style={{ opacity: 0.9, transition: 'opacity 0.3s' }}
                />
                {/* Indicador de imagen */}
                <div className="absolute bottom-1 right-1 bg-black/60 rounded px-1.5 py-0.5 text-[10px] text-zinc-400">
                  📷
                </div>
              </div>
              {/* Botón enviar al área de trabajo - debajo de la imagen.
                  🟥 fix mobile prod (rev3): mostrar si hay CUALQUIER indicio
                  de imagen (image_id, image_url o has_image). En mobile
                  producción a veces el backend solo serializa image_url
                  y el botón quedaba oculto. */}
              {(message.image_id || message.image_url || message.has_image) && (
                <button
                  type="button"
                  disabled={isPublishingToWorkspace}
                  style={{
                    // 🟥 CUADRO NEGRO 2026-06-28 (rev10) — z-index 9999
                    // garantiza que el botón QUEDE POR ENCIMA de cualquier
                    // overlay invisible en mobile (CUADRO NEGRO del usuario).
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'rgba(99, 102, 241, 0.25)',
                    position: 'relative',
                    zIndex: 9999,
                  }}
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // 🟥 Guard anti-doble-fire: si touchstart ya disparó
                    // en los últimos 500ms, ignoramos este click sintético.
                    const now = Date.now();
                    if (now - lastUserImagePublishRef.current < 500) return;
                    lastUserImagePublishRef.current = now;
                    if (isPublishingToWorkspace) return;
                    // Resolver image_id efectivo (image_id directo o
                    // extraído desde image_url).
                    const effectiveId = message.image_id || extractImageIdFromUrl(message.image_url);
                    if (!effectiveId) {
                      toast.error(
                        language === 'es'
                          ? 'No se pudo identificar la imagen. Volvé a subirla.'
                          : 'Could not identify the image. Please re-upload.'
                      );
                      return;
                    }
                    setIsPublishingToWorkspace(true);
                    try {
                      const token = getToken();
                      if (!token || token === 'guest') {
                        toast.error(language === 'es' ? 'Inicia sesión para usar el área de trabajo' : 'Login to use workspace');
                        return;
                      }
                      const res = await axios.post(`${API}/workspace/publish`, {
                        image_id: effectiveId
                      }, {
                      });
                      if (res.data.success) {
                        // Disparar evento con los datos del workspace item para abrirlo
                        window.dispatchEvent(new CustomEvent('workspace-image-published', {
                          detail: {
                            workspaceItem: res.data.workspace_item,
                            imageId: effectiveId,
                            alreadyPublished: res.data.already_published
                          }
                        }));
                        setPublishedToWorkspace(true);
                        toast.success(
                          res.data.already_published
                            ? (language === 'es' ? 'La imagen ya estaba en el área de trabajo' : 'Image already in workspace')
                            : (language === 'es' ? 'Imagen enviada al área de trabajo' : 'Image sent to workspace')
                        );
                      }
                    } catch (err) {
                      if (err.response?.data?.detail?.includes('already') || err.response?.data?.already_published) {
                        setPublishedToWorkspace(true);
                        toast.info(language === 'es' ? 'La imagen ya está en el área de trabajo' : 'Image already in workspace');
                      } else {
                        console.error('Error publishing image:', err);
                        toast.error(language === 'es' ? 'Error al enviar imagen' : 'Error sending image');
                      }
                    } finally {
                      setIsPublishingToWorkspace(false);
                    }
                  }}
                  className={`mt-2 text-sm transition-colors flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-wait min-h-[44px] sm:min-h-0 px-3 py-2 sm:py-1 -mx-2 sm:mx-0 rounded touch-manipulation ${
                    publishedToWorkspace
                      ? 'text-emerald-400 hover:text-emerald-300 active:bg-emerald-500/10'
                      : 'text-zinc-300 hover:text-indigo-400 active:bg-indigo-500/10'
                  }`}
                  data-testid="send-image-to-workspace-btn"
                >
                  {isPublishingToWorkspace ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>{language === 'es' ? 'Enviando...' : 'Sending...'}</span>
                    </>
                  ) : publishedToWorkspace ? (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      <span>{language === 'es' ? 'En Área de Trabajo' : 'In Workspace'}</span>
                    </>
                  ) : (
                    <>
                      <span>↗</span>
                      <span>{language === 'es' ? 'Enviar al Área de Trabajo' : 'Send to Workspace'}</span>
                    </>
                  )}
                </button>
              )}
            </div>
          )}
          <p className="text-sm leading-6 whitespace-pre-wrap">{message.content}</p>
        </div>
        {/* Botones de copiar y borrar para mensaje del usuario */}
        <div className="flex items-center justify-between mt-1.5 mr-1">
          <div className="flex items-center gap-1">
            <button
              onClick={async () => {
                const ok = await copyToClipboard(message.content);
                if (ok) {
                  toast.success(t('messages.copied'));
                } else {
                  toast.error(t('messages.copied') || 'Copy blocked');
                }
              }}
              className="flex items-center gap-1 px-2 py-1 text-xs text-zinc-500 hover:text-cyan-400 hover:bg-cyan-500/10 rounded transition-colors"
              title={t('messages.copyMessage')}
            >
              <Copy className="w-3 h-3" />
            </button>
            {onDeleteMessage && (
              <button
                onClick={() => onDeleteMessage(message)}
                className="flex items-center gap-1 px-2 py-1 text-xs text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                title={t('messages.deleteMessage')}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 ml-2">
            {/* 🟦 V9.5 — Botón "Cerrar" estilo unificado para mensajes del usuario. */}
            {onCollapseMessage && (
              <button
                onClick={onCollapseMessage}
                className="flex items-center gap-1 px-2 py-1 text-xs text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/50 rounded transition-colors"
                title={language === 'es' ? 'Cerrar mensaje' : 'Close message'}
                data-testid={`chat-msg-toggle-cerrar-${messageIdx ?? 'na'}`}
              >
                <X className="w-3 h-3" />
                <span>{language === 'es' ? 'Cerrar' : 'Close'}</span>
              </button>
            )}
            {/* Timestamp del mensaje - alineado a la derecha con tooltip */}
            {formatMessageTimestamp(message.timestamp || message.created_at, language) && (
              <span
                className="text-[11px] text-zinc-500/60 cursor-default"
                title={formatFullTimestamp(message.timestamp || message.created_at, language)}
              >
                {formatMessageTimestamp(message.timestamp || message.created_at, language)}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col" data-testid="ai-message">
      {/* Header with Avatar - Start of message */}
      <div className="flex items-center gap-2 mb-2">
        <Avatar size="sm" />
        <span className="text-sm font-medium text-indigo-400 light:text-indigo-600">DamyxIA</span>
        <div className="flex-1 h-px bg-gradient-to-r from-indigo-500/20 to-transparent" />
      </div>
      
      {/* Message Content */}
      <div className="ml-10 flex-1 max-w-[90%]">
        <div className="text-zinc-200 light:text-zinc-800">
          {message.metadata?.audit_screenshots?.length > 0 ? (
            // 🟥 AUDITORÍA VISUAL E2E — capturas del trabajo activo.
            <AuditScreenshotsBlock
              screenshots={message.metadata.audit_screenshots}
              moduleName={message.metadata.audit_module_name}
              content={message.content}
              apiBase={BACKEND_URL}
              language={language}
            />
          ) : message.project_github_offer ? (
            // 🟥 CUADRO NEGRO V3 — Oferta de importar repositorio de GitHub.
            <>
              <div className="mb-2 text-sm">{message.content}</div>
              <ProjectGitHubImportPrompt
                offer={message.project_github_offer}
                onAccept={async (offer) => {
                  try {
                    const token = getToken();
                    if (!token || token === 'guest') {
                      toast.error(language === 'es' ? 'Inicia sesión para importar desde GitHub' : 'Login to import from GitHub');
                      return false;
                    }
                    const res = await axios.post(
                      `${API}/projects/importFromGitHub`,
                      { url: offer.url },
                      { headers: {} }
                    );
                    if (res.data?.success && res.data?.module_id) {
                      const moduleId = res.data.module_id;
                      const moduleName = res.data.name || offer.repo;
                      window.dispatchEvent(new CustomEvent('damyxia-open-internal-module', {
                        detail: { moduleId, name: moduleName, detectedType: 'project' },
                      }));
                      toast.success(
                        language === 'es'
                          ? `Repo "${moduleName}" importado · ${res.data.file_count} archivos`
                          : `Repo "${moduleName}" imported · ${res.data.file_count} files`
                      );
                      return true;
                    }
                    return false;
                  } catch (error) {
                    const status = error?.response?.status;
                    const detailRaw = error?.response?.data?.detail;
                    const detailStr = typeof detailRaw === 'object'
                      ? (detailRaw?.message || JSON.stringify(detailRaw))
                      : (detailRaw || error?.message || 'unknown');
                    const isPremiumLock = status === 403 && (
                      (typeof detailRaw === 'object' && detailRaw?.code === 'PREMIUM_REQUIRED') ||
                      (typeof detailRaw === 'string' && /premium/i.test(detailRaw))
                    );
                    if (isPremiumLock) {
                      toast.error(
                        language === 'es'
                          ? 'Importar desde GitHub es exclusivo Premium.'
                          : 'GitHub import is Premium-only.',
                        {
                          duration: 6000,
                          action: {
                            label: language === 'es' ? 'Actualizar' : 'Upgrade',
                            onClick: navigateToPremium,
                          },
                        }
                      );
                    } else {
                      // Mensajes específicos por tipo de error backend.
                      const errMap = {
                        invalid_github_url: language === 'es' ? 'URL de GitHub inválida' : 'Invalid GitHub URL',
                        repo_not_found: language === 'es' ? 'Repositorio no encontrado o privado' : 'Repository not found or private',
                        repo_forbidden: language === 'es' ? 'Acceso al repositorio denegado' : 'Repository access forbidden',
                        repo_zip_too_large: language === 'es' ? 'El repositorio es demasiado grande' : 'Repository too large',
                        no_text_files_in_repo: language === 'es' ? 'El repositorio no tiene archivos de texto importables' : 'Repo has no importable text files',
                        github_zip_corrupt: language === 'es' ? 'El ZIP del repositorio está dañado' : 'Repository ZIP is corrupted',
                      };
                      const friendly = errMap[detailStr] || detailStr;
                      toast.error(
                        language === 'es'
                          ? `No se pudo importar: ${friendly}`
                          : `Import failed: ${friendly}`
                      );
                    }
                    return false;
                  }
                }}
                onDecline={() => {
                  toast.info(
                    language === 'es'
                      ? 'OK, no importé el repo.'
                      : 'OK, repo not imported.'
                  );
                }}
              />
            </>
          ) : message.project_offer ? (
            // 🟥 CUADRO NEGRO 2026-02 — Oferta de adaptar proyecto al Workspace.
            // No reemplaza al texto del mensaje (lo mostramos arriba del prompt
            // como saludo breve). Botones Sí/No disparan /api/projects/build.
            <>
              <div className="mb-2 text-sm">{message.content}</div>
              <ProjectAdaptPrompt
                offer={message.project_offer}
                onAccept={async (offer) => {
                  try {
                    const token = getToken();
                    if (!token || token === 'guest') {
                      toast.error(language === 'es' ? 'Inicia sesión para usar el área de trabajo' : 'Login to use workspace');
                      return false;
                    }
                    const res = await axios.post(
                      `${API}/projects/build`,
                      {
                        message: offer.rawMessage,
                        projectName: offer.suggestedName || undefined,
                        language,
                      },
                      { headers: {} }
                    );
                    if (res.data?.success && res.data?.module_id) {
                      const moduleId = res.data.module_id;
                      const moduleName = res.data.name || offer.suggestedName || 'Project';
                      window.dispatchEvent(new CustomEvent('damyxia-open-internal-module', {
                        detail: { moduleId, name: moduleName, detectedType: 'project' },
                      }));
                      toast.success(
                        language === 'es'
                          ? `Proyecto "${moduleName}" creado · ${res.data.file_count} archivos`
                          : `Project "${moduleName}" created · ${res.data.file_count} files`
                      );
                      return true;
                    }
                    return false;
                  } catch (error) {
                    const status = error?.response?.status;
                    const detailRaw = error?.response?.data?.detail;
                    const isPremiumLock = status === 403 && (
                      (typeof detailRaw === 'object' && detailRaw?.code === 'PREMIUM_REQUIRED') ||
                      (typeof detailRaw === 'string' && /premium/i.test(detailRaw))
                    );
                    if (isPremiumLock) {
                      toast.error(
                        language === 'es'
                          ? 'Adaptar proyectos es exclusivo Premium. Actualiza tu plan.'
                          : 'Adapting projects is Premium-only. Upgrade your plan.',
                        {
                          duration: 6000,
                          action: {
                            label: language === 'es' ? 'Actualizar' : 'Upgrade',
                            onClick: navigateToPremium,
                          },
                        }
                      );
                    } else {
                      const detail = typeof detailRaw === 'object' ? (detailRaw?.message || 'unknown') : (detailRaw || error?.message || 'unknown');
                      toast.error(
                        language === 'es'
                          ? `Error al adaptar el proyecto: ${detail}`
                          : `Error adapting project: ${detail}`
                      );
                    }
                    return false;
                  }
                }}
                onDecline={() => {
                  toast.info(
                    language === 'es'
                      ? 'OK, seguimos en el chat normal.'
                      : 'OK, continuing in chat.'
                  );
                }}
              />
            </>
          ) : message.review && Array.isArray(message.review.sections) && message.review.sections.length > 0 ? (
            <ReviewBox
              review={message.review}
              onConvertToModule={async ({ title: reviewTitle, rawText }) => {
                // Convierte la revisión en un módulo editable en el
                // Área de Trabajo Interna usando el mismo endpoint que
                // el botón "Enviar al Área de Trabajo" para mensajes de
                // texto. No rompe ningún flujo: si falla por Premium o
                // error de red, mostramos el toast estándar y devolvemos
                // false. La conversación y el ReviewBox quedan intactos.
                try {
                  const token = getToken();
                  if (!token || token === 'guest') {
                    toast.error(language === 'es' ? 'Inicia sesión para usar el área de trabajo' : 'Login to use workspace');
                    return false;
                  }
                  const headers = {};
                  const res = await axios.post(
                    `${API}/workspace/auto`,
                    {
                      content: rawText,
                      title: reviewTitle,
                      source: 'review',
                    },
                    { headers }
                  );
                  const moduleId = res.data?.module_id;
                  const moduleName = res.data?.name || reviewTitle || 'Revisión';
                  const detected = res.data?.detected_type || 'text';
                  window.dispatchEvent(new CustomEvent('damyxia-open-internal-module', {
                    detail: { moduleId, name: moduleName, detectedType: detected },
                  }));
                  toast.success(
                    language === 'es'
                      ? `Revisión convertida en módulo "${moduleName}"`
                      : `Review converted into module "${moduleName}"`
                  );
                  return true;
                } catch (error) {
                  const status = error?.response?.status;
                  const detailRaw = error?.response?.data?.detail;
                  const isPremiumLock = status === 403 && (
                    (typeof detailRaw === 'object' && detailRaw?.code === 'PREMIUM_REQUIRED') ||
                    (typeof detailRaw === 'string' && /premium/i.test(detailRaw))
                  );
                  if (isPremiumLock) {
                    toast.error(
                      language === 'es'
                        ? 'Convertir a módulo es exclusivo Premium. Actualiza tu plan.'
                        : 'Convert to module is Premium-only. Upgrade your plan.',
                      {
                        duration: 6000,
                        action: {
                          label: language === 'es' ? 'Actualizar' : 'Upgrade',
                          onClick: navigateToPremium,
                        },
                      }
                    );
                  } else {
                    const detail = typeof detailRaw === 'object' ? (detailRaw?.message || 'unknown') : (detailRaw || error?.message || 'unknown');
                    toast.error(
                      language === 'es'
                        ? `Error al convertir a módulo: ${detail}`
                        : `Error converting to module: ${detail}`
                    );
                  }
                  return false;
                }
              }}
            />
          ) : isNewMessage ? (
            <StreamingText content={displayContent} isNew={true} />
          ) : (
            <MessageContent content={displayContent} />
          )}

          {/* 🟥 CUADRO NEGRO — Badge de acción estructurada del Workspace
              con estado de auto-apertura (running / done / error). */}
          {hasDamyxiaAction && damyxiaActionPreview && (
            <div
              data-testid="damyxia-action-badge"
              data-fire-state={autoFireState}
              className={
                "mt-3 flex items-center gap-2 rounded-lg border px-3 py-2 text-xs " +
                (autoFireState === 'done'
                  ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-100"
                  : autoFireState === 'error'
                  ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                  : "border-emerald-500/30 bg-emerald-500/10 text-emerald-200")
              }
            >
              {autoFireState === 'running' ? (
                <Loader2 className="w-4 h-4 text-emerald-300 animate-spin" />
              ) : autoFireState === 'done' ? (
                <Check className="w-4 h-4 text-emerald-300" />
              ) : (
                <SendHorizontal className="w-4 h-4 text-emerald-300" />
              )}
              <span className="font-semibold">{damyxiaActionPreview.typeLabel}:</span>
              <span className="text-emerald-100/90 truncate">{damyxiaActionPreview.title}</span>
              <span className="ml-auto text-[10px] uppercase tracking-widest text-emerald-300/70">
                {autoFireState === 'running'
                  ? (language === 'es' ? 'Creando…' : 'Creating…')
                  : autoFireState === 'done'
                  ? (language === 'es' ? 'Abierto en Workspace' : 'Opened in Workspace')
                  : autoFireState === 'error'
                  ? (language === 'es' ? 'Procesando…' : 'Processing…')
                  : (language === 'es' ? 'Auto-envío' : 'Auto-send')}
              </span>
            </div>
          )}
          
          {/* Show generated image(s) if present */}
          {(message.image_base64 || message.image_variations) && (
            <ImageGallery 
              images={message.image_variations} 
              singleImage={message.image_base64}
            />
          )}
          
          {/* Code Analysis Section */}
          {hasCode && (
            <div className="mt-4">
              {isAnalyzing && (
                <div className="flex items-center gap-2 text-sm text-zinc-400 py-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <Sparkles className="w-4 h-4 text-indigo-400" />
                  <span>{t('messages.analyzingCode')}</span>
                </div>
              )}
              
              {codeAnalysis && (
                <CodeAnalysisResults 
                  analysis={codeAnalysis}
                  codeBlocks={codeBlocks}
                  onApplyFix={onCodeFix ? handleApplyFix : null}
                />
              )}
            </div>
          )}
          
          {/* Botones de Ejecutar, Corregir, Editar, Copiar, Salvar, PDF y Borrar */}
          <div className="flex items-center gap-2 mt-3 pt-2 border-t border-zinc-800/30 flex-wrap">
            {/* Ejecutar código - Solo visible cuando hay código React/JSX */}
            {showExecuteButton && (
              <button
                onClick={handleExecuteCode}
                className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
                  codeExecuted 
                    ? 'text-emerald-400 hover:bg-emerald-500/10' 
                    : 'text-zinc-500 hover:text-indigo-400 hover:bg-indigo-500/10'
                }`}
                title={t('messages.executeInWorkspace')}
                data-testid="execute-code-btn"
              >
                {codeExecuted ? (
                  <>
                    <Check className="w-3 h-3" />
                    <span>{t('messages.sent')}</span>
                  </>
                ) : (
                  <>
                    <Play className="w-3 h-3" />
                    <span>{t('messages.execute')}</span>
                  </>
                )}
              </button>
            )}
            
            {/* Corregir errores - Solo visible cuando hay código React/JSX */}
            {showExecuteButton && (
              <button
                onClick={() => {
                  // Enviar mensaje a DamyxIA para corregir errores
                  if (onCodeFix) {
                    onCodeFix(message.content);
                  }
                  toast.success(t('messages.requestingFix'));
                }}
                className="flex items-center gap-1 px-2 py-1 text-xs text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10 rounded transition-colors"
                title={t('messages.askToFix')}
                data-testid="fix-errors-btn"
              >
                <Wrench className="w-3 h-3" />
                <span>{t('messages.fix')}</span>
              </button>
            )}
            
            {/* Editar código - Solo visible cuando hay código React/JSX */}
            {showExecuteButton && !isEditingCode && (
              <button
                onClick={handleStartEditing}
                className="flex items-center gap-1 px-2 py-1 text-xs text-zinc-500 hover:text-purple-400 hover:bg-purple-500/10 rounded transition-colors"
                title={t('messages.editBeforeExecute')}
                data-testid="edit-code-btn"
              >
                <Edit3 className="w-3 h-3" />
                <span>{t('messages.edit')}</span>
              </button>
            )}
            
            {/* Enviar al Área de Trabajo (assistant only).
                Aparece cuando hay imagen y/o texto: el handler decide
                si publicar la imagen o convertir el texto en modulo. */}
            {message.role === 'assistant' && (message.content || message.image_id) && (
              <button
                onClick={handleSendToWorkspace}
                disabled={isSendingToWorkspace}
                style={{
                  // 🟥 CUADRO NEGRO 2026-06-28 (rev10) — z-index 9999
                  // por encima de cualquier overlay invisible en mobile.
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'rgba(16, 185, 129, 0.25)',
                  position: 'relative',
                  zIndex: 9999,
                }}
                className={`flex items-center gap-1 px-2.5 sm:px-2 py-2 sm:py-1 text-xs rounded transition-colors touch-manipulation min-h-[44px] sm:min-h-0 ${
                  sentToWorkspace
                    ? 'text-emerald-400 hover:bg-emerald-500/10 active:bg-emerald-500/20'
                    : 'text-zinc-500 hover:text-emerald-400 hover:bg-emerald-500/10 active:bg-emerald-500/20'
                } disabled:opacity-60 disabled:cursor-wait`}
                title={language === 'es' ? 'Enviar al Área de Trabajo' : 'Send to Workspace'}
                data-testid="send-to-workspace-btn"
              >
                {isSendingToWorkspace ? (
                  <span className="flex items-center gap-1" aria-label="processing">
                    <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-bounce" style={{ animationDelay: '120ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-bounce" style={{ animationDelay: '240ms' }} />
                  </span>
                ) : sentToWorkspace ? (
                  <>
                    <Check className="w-3 h-3" />
                    <span>{language === 'es' ? 'Enviado' : 'Sent'}</span>
                  </>
                ) : (
                  <>
                    <SendHorizontal className="w-3 h-3" />
                    <span>{language === 'es' ? 'Enviar' : 'Send'}</span>
                  </>
                )}
              </button>
            )}

            {/* Copiar */}
            <button
              onClick={() => {
                const text = message.content;
                if (navigator.clipboard && navigator.clipboard.writeText) {
                  navigator.clipboard.writeText(text).then(() => {
                    setCopied(true);
                    toast.success(t('messages.copied'));
                    setTimeout(() => setCopied(false), 2000);
                  }).catch(() => {
                    // Fallback
                    const textarea = document.createElement('textarea');
                    textarea.value = text;
                    document.body.appendChild(textarea);
                    textarea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textarea);
                    setCopied(true);
                    toast.success(t('messages.copied'));
                    setTimeout(() => setCopied(false), 2000);
                  });
                }
              }}
              className="flex items-center gap-1 px-2 py-1 text-xs text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/50 rounded transition-colors"
              title={t('messages.copyResponse')}
              data-testid="copy-message-btn"
            >
              {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
              <span>{copied ? t('messages.copied') : t('messages.copy')}</span>
            </button>

            {/* 🟦 V9.5.2 — Borrar (movido aquí, inmediatamente después de Copiar) */}
            {onDeleteMessage && (
              <button
                onClick={() => onDeleteMessage(message)}
                className="flex items-center gap-1 px-2 py-1 text-xs text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                title={t('messages.deleteMessage')}
                data-testid="delete-message-btn"
              >
                <Trash2 className="w-3 h-3" />
                <span>{t('common.delete')}</span>
              </button>
            )}
            
            {/* Salvar */}
            <button
              onClick={() => {
                const text = message.content;
                const blob = new Blob([text], { type: 'text/plain' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `damyxia_respuesta_${Date.now()}.txt`;
                a.click();
                URL.revokeObjectURL(url);
                toast.success(t('messages.saved'));
              }}
              className="flex items-center gap-1 px-2 py-1 text-xs text-zinc-500 hover:text-green-400 hover:bg-green-500/10 rounded transition-colors"
              title={t('messages.saveResponse')}
              data-testid="save-message-btn"
            >
              <Save className="w-3 h-3" />
              <span>{t('messages.save')}</span>
            </button>

            {/* 🟦 V9.5.2 — Cerrar (movido aquí, inmediatamente después de Salvar) */}
            {onCollapseMessage && (
              <button
                onClick={onCollapseMessage}
                className="flex items-center gap-1 px-2 py-1 text-xs text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/50 rounded transition-colors"
                title={language === 'es' ? 'Cerrar mensaje' : 'Close message'}
                data-testid={`chat-msg-toggle-cerrar-${messageIdx ?? 'na'}`}
              >
                <X className="w-3 h-3" />
                <span>{language === 'es' ? 'Cerrar' : 'Close'}</span>
              </button>
            )}
            
            {/* Guardar imagen en Galería - Solo cuando hay imagen generada */}
            {(message.image_base64 || message.image_variations) && (
              <button
                onClick={async () => {
                  const imageData = message.image_base64 || (message.image_variations && message.image_variations[0]);
                  if (!imageData) return;
                  
                  const base64 = typeof imageData === 'string' ? imageData : imageData.base64 || imageData;
                  const prompt = message.content?.substring(0, 200) || 'Imagen generada por DamyxIA';
                  
                  const success = await saveImageToGallery({ base64, prompt });
                  if (success) {
                    setImageSaved(true);
                    toast.success(language === 'es' ? 'Imagen guardada en la galeria' : 'Image saved to gallery');
                    window.dispatchEvent(new Event('gallery-updated'));
                    setTimeout(() => setImageSaved(false), 3000);
                  } else {
                    toast.error(language === 'es' ? 'Error al guardar imagen' : 'Error saving image');
                  }
                }}
                disabled={imageSaved}
                className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
                  imageSaved
                    ? 'text-emerald-400 bg-emerald-500/10'
                    : 'text-zinc-500 hover:text-indigo-400 hover:bg-indigo-500/10'
                }`}
                title={language === 'es' ? 'Guardar imagen en galeria' : 'Save image to gallery'}
                data-testid="save-image-gallery-btn"
              >
                {imageSaved ? <Check className="w-3 h-3" /> : <ImagePlus className="w-3 h-3" />}
                <span>{imageSaved ? (language === 'es' ? 'Guardada' : 'Saved') : (language === 'es' ? 'Guardar' : 'Save img')}</span>
              </button>
            )}
            
            {/* Descargar PDF - Solo para tutoriales/guías */}
            {showPDFButton && (
              <button
                onClick={handleExportPDF}
                disabled={isExportingPDF}
                className="flex items-center gap-1 px-2 py-1 text-xs text-zinc-500 hover:text-indigo-400 hover:bg-indigo-500/10 rounded transition-colors disabled:opacity-50"
                title={t('messages.downloadAsPdf')}
                data-testid="download-pdf-btn"
              >
                {isExportingPDF ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <FileText className="w-3 h-3" />
                )}
                <span>{isExportingPDF ? t('messages.generating') : 'PDF'}</span>
              </button>
            )}
            
            {/* Borrar — movido a la posición V9.5.2 después de Copiar */}
          </div>
          
          {/* Editor de Código Inline */}
          {isEditingCode && (
            <div className="mt-4 border border-amber-500/30 rounded-xl overflow-hidden bg-zinc-900/80" data-testid="code-editor-panel">
              {/* Header del Editor */}
              <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-r from-amber-900/30 to-zinc-900 border-b border-amber-500/20">
                <div className="flex items-center gap-2">
                  <Edit3 className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-medium text-amber-300">
                    {t('messages.codeEditor')}
                  </span>
                  <span className="text-xs text-zinc-500">
                    {t('messages.modifyAndExecute')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {/* Restaurar original */}
                  <button
                    onClick={handleResetCode}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700/50 rounded transition-colors"
                    title={t('messages.resetToOriginal')}
                    data-testid="reset-code-btn"
                  >
                    <RotateCcw className="w-3 h-3" />
                    <span>{t('messages.reset')}</span>
                  </button>
                  {/* Cerrar editor */}
                  <button
                    onClick={handleCancelEditing}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                    title={t('messages.closeEditor')}
                    data-testid="close-editor-btn"
                  >
                    <X className="w-3 h-3" />
                    <span>{t('messages.close')}</span>
                  </button>
                </div>
              </div>
              
              {/* Área de Edición */}
              <div className="relative">
                <textarea
                  value={editedCode}
                  onChange={(e) => setEditedCode(e.target.value)}
                  className="w-full h-64 p-4 bg-zinc-950 text-zinc-100 font-mono text-sm resize-y focus:outline-none focus:ring-1 focus:ring-amber-500/50"
                  placeholder={t('messages.writeOrModifyCode')}
                  spellCheck={false}
                  data-testid="code-editor-textarea"
                  style={{
                    tabSize: 2,
                    lineHeight: '1.6',
                  }}
                />
                {/* Indicador de líneas */}
                <div className="absolute bottom-2 right-2 text-xs text-zinc-600">
                  {editedCode.split('\n').length} {t('messages.lines')}
                </div>
              </div>
              
              {/* Footer del Editor con botones de acción */}
              <div className="flex items-center justify-between px-4 py-3 bg-zinc-900/50 border-t border-zinc-800">
                <div className="text-xs text-zinc-500">
                  {editedCode !== originalCode && (
                    <span className="text-amber-400">
                      {t('messages.codeModified')}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {/* Copiar código editado */}
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(editedCode);
                      toast.success(t('messages.codeCopied'));
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-zinc-700/50 text-zinc-300 hover:bg-zinc-700 transition-colors"
                    data-testid="copy-edited-code-btn"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    <span>{t('messages.copy')}</span>
                  </button>
                  {/* Ejecutar código editado */}
                  <button
                    onClick={handleExecuteEditedCode}
                    disabled={!editedCode.trim()}
                    className={`flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium rounded-lg transition-all ${
                      codeExecuted 
                        ? 'bg-emerald-600/30 text-emerald-400 border border-emerald-500/50' 
                        : 'bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed'
                    }`}
                    data-testid="execute-edited-code-btn"
                  >
                    {codeExecuted ? (
                      <>
                        <Check className="w-3.5 h-3.5" />
                        <span>{t('messages.executed')}</span>
                      </>
                    ) : (
                      <>
                        <Play className="w-3.5 h-3.5" />
                        <span>{t('messages.executeCode')}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Footer with Avatar - End of message */}
      <div className="flex items-center gap-2 mt-3 ml-10">
        <div className="flex-1 h-px bg-gradient-to-l from-indigo-500/20 to-transparent" />
        <span className="text-xs text-zinc-500 light:text-zinc-400">
          {t('messages.responseFrom')}
        </span>
        <Avatar size="sm" />
      </div>
      
      {/* Timestamp del mensaje - alineado a la izquierda con tooltip */}
      {formatMessageTimestamp(message.timestamp || message.created_at, language) && (
        <div className="ml-10 mt-1">
          <span 
            className="text-[11px] text-zinc-500/60 cursor-default"
            title={formatFullTimestamp(message.timestamp || message.created_at, language)}
          >
            {formatMessageTimestamp(message.timestamp || message.created_at, language)}
          </span>
        </div>
      )}
    </div>
  );
};

export default MessageBubble;
